import meridianColors from "@amzn/meridian-tokens/base/color";
import downloadLargeTokens from "@amzn/meridian-tokens/base/icon/download-large";
import Breadcrumb, { BreadcrumbGroup } from "@amzn/meridian/breadcrumb";
import Button from "@amzn/meridian/button";
import Coachmark from "@amzn/meridian/coachmark";
import Column from "@amzn/meridian/column";
import Expander from "@amzn/meridian/expander";
import FileInput from "@amzn/meridian/file-input";
import Icon from "@amzn/meridian/icon";
import Input from "@amzn/meridian/input";
import Link from "@amzn/meridian/link";
import Menu, { MenuItem } from "@amzn/meridian/menu";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Text from "@amzn/meridian/text";
import Toggle from "@amzn/meridian/toggle";
import _ from "lodash";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PaginationWithDropdown from "src/components/pages/audit/paginationWithDropdown";
import {
  findNextUnreviewedCase,
  isAssignedBasedReview,
  getReviewedBy,
  getValidatedBy,
  getPendingReviewers,
  getPendingValidators,
} from "src/components/shared/groundTruth/reviewTable/helpers";
import {
  downloadGroundTruthsInCSV,
  getGroundTruthDataListView,
  resetListViewPage,
  setCurrentPage,
  setIsBlind,
  setItemPerPage,
  sortAndFilterGroundTruthDataListView,
  setAliasFilter,
  applyAliasFilter,
  assignReviewJobs,
  updateAssignment,
  ReviewerAssignmentTypeEnum,
} from "src/store/modelLifeCycleGroundTruthSlice";
import {
  Model,
  resetGetSiteDetailsLoading,
  setSelectedModelId,
} from "src/store/modelManagementSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { isValidRange } from "src/utils/numberUtil";
import { hasModelPermission, isAdminUser } from "src/utils/permissionUtil";
import DonutChart from "src/components/shared/donutChart/donutChart";
import "./modelGroundTruthListView.scss";
import {
  getGroundTruthDataListViewReviewer,
  getGroundTruthDataListViewStatus,
} from "src/components/shared/groundTruth/listView/listViewUtils";

const ModelGroundTruthListView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { modelId } = useParams();

  const {
    groundTruthDataListView,
    filteredGroundTruthDataListView,
    aliasFilter,
    currentPage,
    isBlind,
    itemsPerPage,
    getListViewLoading,
    groundTruthDataTotalCount,
    groundTruthDataTotalReviewedCount,
    assignedReviewTasks,
    pendingReviewTasks,
    assignedValidationTasks,
    pendingValidationTasks,
  } = useAppSelector((state) => state.modelLifeCycleGroundTruthSlice);

  const { token, alias, ldap } = useAppSelector((state) => state.user);
  const { selectedModel } = useAppSelector((state) => state.modelSlice);

  // Local state for CSV upload and preview modal
  const [assignmentCSV, setAssignmentCSV] = useState<File | null>(null);
  const [showUploadGuideModal, setShowUploadGuideModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [assignmentPreview, setAssignmentPreview] = useState<any[]>([]);

  // Local state for update assignment modal
  const [showUpdateAssignmentModal, setShowUpdateAssignmentModal] = useState(false);
  const [updateFormData, setUpdateFormData] = useState({
    oldReviewer: "",
    newReviewer: "",
    assignmentType: "Replace" as "Replace" | "Remove",
  });

  // Menu state for actions menu
  const actionsMenuRef = useRef();
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

  // Function to parse CSV and generate preview
  const parseCSVForPreview = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split("\n").filter((line) => line.trim());
      const headers = lines[0].split(",").map((h) => h.trim());

      const preview: Record<string, { regular: number; admin: number }> = {};

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map((v) => v.trim());
        const rowData: Record<string, string> = {};
        headers.forEach((header, index) => {
          rowData[header] = values[index] || "";
        });

        // Parse regular reviewers
        if (rowData.reviewers) {
          const reviewers = rowData.reviewers
            .split(";")
            .map((r) => r.trim())
            .filter((r) => r);
          reviewers.forEach((reviewer) => {
            if (!preview[reviewer]) preview[reviewer] = { regular: 0, admin: 0 };
            preview[reviewer].regular += 1;
          });
        }

        // Parse admin reviewers
        if (rowData.admin_reviewers) {
          const adminReviewers = rowData.admin_reviewers
            .split(";")
            .map((r) => r.trim())
            .filter((r) => r);
          adminReviewers.forEach((reviewer) => {
            if (!preview[reviewer]) preview[reviewer] = { regular: 0, admin: 0 };
            preview[reviewer].admin += 1;
          });
        }
      }

      // Convert to array for display
      const previewArray = Object.entries(preview).map(([reviewer, counts]) => ({
        reviewer,
        regularTasks: counts.regular,
        adminTasks: counts.admin,
        totalTasks: counts.regular + counts.admin,
      }));

      setAssignmentPreview(previewArray);
      setShowPreviewModal(true);
    };
    reader.readAsText(file);
  };

  // Handle CSV file upload
  const handleCSVUpload = (files: File[]) => {
    const file = files[0];
    setAssignmentCSV(file);
    parseCSVForPreview(file);
  };

  // Handle assignment confirmation
  const handleConfirmAssignment = () => {
    if (assignmentCSV && modelId) {
      dispatch(
        assignReviewJobs({
          modelId: modelId,
          file: assignmentCSV,
        }),
      );
      setShowPreviewModal(false);
      setAssignmentCSV(null);
    }
  };

  // Handle create assignment menu item click
  const handleCreateAssignmentClick = () => {
    setShowUploadGuideModal(true);
    setActionsMenuOpen(false);
  };

  // Handle update assignment menu item click
  const handleUpdateAssignmentClick = () => {
    setShowUpdateAssignmentModal(true);
    setActionsMenuOpen(false);
  };

  // Handle download assignment menu item click
  const handleDownloadAssignmentClick = async () => {
    if (!groundTruthDataListView?.length) return;
    setActionsMenuOpen(false);

    const headers = [
      "source_id",
      "pending_review",
      "pending_validation",
      "reviewed_by",
      "validated_by",
    ];
    const rows = groundTruthDataListView.map((item) => {
      const { sourceId, pendingReviewers, adminReviewers, reviewContributors } = item;
      const pendingReview = (pendingReviewers || [])
        .filter((reviewer) => !(adminReviewers || []).includes(reviewer))
        .join(";");
      const pendingValidation = (pendingReviewers || [])
        .filter((reviewer) => (adminReviewers || []).includes(reviewer))
        .join(";");
      const reviewedBy = (reviewContributors || [])
        .filter((contributor) => !(adminReviewers || []).includes(contributor))
        .join(";");
      const validatedBy = (reviewContributors || [])
        .filter((contributor) => (adminReviewers || []).includes(contributor))
        .join(";");
      return [sourceId || "", pendingReview, pendingValidation, reviewedBy, validatedBy];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((field) => {
            let processedField = field || "";
            return processedField.includes(",") ? `"${processedField}"` : processedField;
          })
          .join(","),
      ),
    ].join("\n");

    const date = new Date().toISOString().split("T")[0];
    const filename = `ground_truth_review_allocation_${date}.csv`;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const { downloadFile } = await import("src/utils/fileUtil");
    downloadFile(url, filename);
  };

  // Handle review button click
  const handleReviewClick = () => {
    const next = findNextUnreviewedCase(groundTruthDataListView, alias);
    if (next) {
      navigate(`/validationWorkflow/${modelId}/${next.sourceId}`);
    } else {
      navigate(`/validationWorkflow/${modelId}/${groundTruthDataListView[0].sourceId}`);
    }
  };

  // Handle export ground truths button click
  const handleExportGroundTruthsClick = () => {
    if (modelId) {
      dispatch(downloadGroundTruthsInCSV({ modelId: modelId }));
    }
  };

  // triggerred when review page redirect to list view
  useEffect(() => {
    dispatch(resetListViewPage());
    dispatch(resetGetSiteDetailsLoading());
  }, []);

  useEffect(() => {
    if (modelId && token && getListViewLoading !== "fulfilled") {
      dispatch(getGroundTruthDataListView(modelId));
    }
  }, [modelId, token]);

  useEffect(() => {
    if (modelId) {
      dispatch(setSelectedModelId(modelId));
    }
  }, [modelId]);

  useEffect(() => {
    if (getListViewLoading === "rejected") {
      navigate(`/validationWorkflow`);
    } else if (getListViewLoading === "fulfilled" && selectedModel.id) {
      dispatch(
        sortAndFilterGroundTruthDataListView({
          model: selectedModel,
          alias: alias,
          isAdmin: isAdminUser(ldap),
        }),
      );
      // Apply any existing filter after data is loaded
      dispatch(applyAliasFilter());
    }
  }, [getListViewLoading, selectedModel]);

  // Helper function to determine if user should see enhanced admin view
  const shouldShowEnhancedView = () => {
    return isAssignedBasedReview(selectedModel) && isAdminUser(ldap);
  };

  // Get unique aliases from ground truth data for dropdown options
  const aliasOptions = React.useMemo(() => {
    const allAliases = new Set<string>();
    groundTruthDataListView.forEach((item) => {
      item.reviewContributors?.forEach((alias) => allAliases.add(alias.toLowerCase()));
      item.pendingReviewers?.forEach((alias) => allAliases.add(alias.toLowerCase()));
      item.adminReviewers?.forEach((alias) => allAliases.add(alias.toLowerCase()));
    });
    // Filter out 'llm' and return sorted array
    return Array.from(allAliases)
      .filter((alias) => alias !== "llm")
      .sort()
      .map((alias) => ({ label: alias, value: alias }));
  }, [groundTruthDataListView]);

  // Calculate metrics based on current filter selection
  const calculateMetrics = React.useMemo(() => {
    if (shouldShowEnhancedView() && aliasFilter.length > 0) {
      // When filtering by specific reviewers, calculate their specific metrics
      let totalAssigned = 0;
      let totalPending = 0;
      let totalCompleted = 0;

      aliasFilter.forEach((selectedAlias) => {
        // Add review tasks for this reviewer
        const reviewAssigned = assignedReviewTasks[selectedAlias] || 0;
        const reviewPending = pendingReviewTasks[selectedAlias] || 0;
        totalAssigned += reviewAssigned;
        totalPending += reviewPending;
        totalCompleted += reviewAssigned - reviewPending;

        // Add validation tasks for this reviewer
        const validationAssigned = assignedValidationTasks[selectedAlias] || 0;
        const validationPending = pendingValidationTasks[selectedAlias] || 0;
        totalAssigned += validationAssigned;
        totalPending += validationPending;
        totalCompleted += validationAssigned - validationPending;
      });

      return {
        totalCount: totalAssigned,
        completedCount: totalCompleted,
        title: `Selected Reviewers Progress`,
        subtitle: `${aliasFilter.join(", ")} - Tasks completed`,
      };
    } else {
      // Default: show aggregate progress for all tasks
      const totalAssignedReviews = Object.values(assignedReviewTasks).reduce(
        (sum, count) => sum + count,
        0,
      );
      const totalPendingReviews = Object.values(pendingReviewTasks).reduce(
        (sum, count) => sum + count,
        0,
      );
      const totalAssignedValidations = Object.values(assignedValidationTasks).reduce(
        (sum, count) => sum + count,
        0,
      );
      const totalPendingValidations = Object.values(pendingValidationTasks).reduce(
        (sum, count) => sum + count,
        0,
      );

      const totalTasks = totalAssignedReviews + totalAssignedValidations;
      const totalPendingTasks = totalPendingReviews + totalPendingValidations;
      const totalCompletedTasks = totalTasks - totalPendingTasks;

      return {
        totalCount: totalTasks,
        completedCount: totalCompletedTasks,
        title: "Overall Progress",
        subtitle: "All assigned tasks completed",
      };
    }
  }, [
    aliasFilter,
    assignedReviewTasks,
    pendingReviewTasks,
    assignedValidationTasks,
    pendingValidationTasks,
    shouldShowEnhancedView(),
  ]);

  // Determine which data to display (filtered or original)
  const displayData =
    shouldShowEnhancedView() && aliasFilter.length > 0
      ? filteredGroundTruthDataListView
      : groundTruthDataListView;

  const numberOfPages = Math.ceil(displayData.length / itemsPerPage);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = Math.min(displayData.length, firstVisibleIndex + itemsPerPage - 1);

  // Dynamic table headers based on review type and user permissions
  const tableHeaderRowNames = shouldShowEnhancedView()
    ? ["Source ID", "Pending Reviewers", "Pending Validators", "Reviewed By", "Validated By"]
    : ["Source ID", "Status", "Reviewer"];

  const ControlledExpander = (props: any) => {
    const [open, setOpen] = useState(true);
    return <Expander open={open} onChange={setOpen} {...props} />;
  };
  return (
    <Row widths={["75%", "25%"]}>
      <Column spacingInset={"0 500 500 500"}>
        <BreadcrumbGroup>
          <Breadcrumb onClick={() => navigate(`/validationWorkflow`)}>
            Validation workflow
          </Breadcrumb>
          <Breadcrumb onClick={() => navigate(`/validationWorkflow/${modelId}`)}>
            {selectedModel.name} - Processed
          </Breadcrumb>
        </BreadcrumbGroup>
        <Row widths={["65%", "35%"]}>
          <Row spacing={"400"}>
            <Column alignmentHorizontal="start" spacing={"300"}>
              <Text type={"d50"}>Ground Truth - Processed</Text>
              <Row spacing={"600"}>
                <Text type="b100">Model Id: {modelId}</Text>
                <Text type="b100">Total Fields: {Object.keys(selectedModel.fields).length}</Text>
              </Row>
            </Column>
          </Row>
          <Row alignmentHorizontal="end" widths={["50%"]}>
            <Row alignmentHorizontal="end" spacing="400">
              {/* Actions Menu - Admin Only */}
              {shouldShowEnhancedView() && (
                <React.Fragment>
                  <Button
                    ref={actionsMenuRef}
                    type="tertiary"
                    onClick={() => setActionsMenuOpen(true)}
                    aria-haspopup="menu"
                    aria-expanded={actionsMenuOpen}
                  >
                    Manage Assignment
                  </Button>
                  <Menu
                    anchorNode={actionsMenuRef.current}
                    id="review-actions-menu"
                    open={actionsMenuOpen}
                    position="bottom"
                    onClose={() => setActionsMenuOpen(false)}
                  >
                    <MenuItem onClick={handleCreateAssignmentClick}>Create Assignment</MenuItem>
                    <MenuItem onClick={handleUpdateAssignmentClick}>Update Assignment</MenuItem>
                    <MenuItem onClick={handleDownloadAssignmentClick}>Download Assignment</MenuItem>
                  </Menu>
                </React.Fragment>
              )}

              {hasModelPermission(selectedModel, alias, ldap) && (
                <Button
                  type="tertiary"
                  onClick={handleExportGroundTruthsClick}
                  data-cy={"download-ground-truths__button"}
                >
                  <Icon tokens={downloadLargeTokens}></Icon>
                  Export Ground Truths
                </Button>
              )}
              <Button onClick={handleReviewClick} disabled={groundTruthDataListView.length === 0}>
                Review
              </Button>
            </Row>
            <Column spacing={"none"}>
              <Toggle
                checked={isBlind}
                onChange={() => {
                  dispatch(setIsBlind(!isBlind));
                }}
              />
              <Text>Blinding</Text>
            </Column>
          </Row>
        </Row>
        <Column height="65vh" overflowY="auto">
          <Table showDividers={true} fixHeaderRows={true} stickyHeaderColumn={true} headerRows={1}>
            <TableRow backgroundColor={meridianColors.colorGray50}>
              {tableHeaderRowNames.map((name) => (
                <TableCell>
                  {name === "Insights Generated" ? (
                    <Row spacing={"none"}>
                      <Coachmark popoverPosition="top" animating={false}>
                        <Text>
                          The number represents: number of field extracted by LLM excluding
                          'inadequate' result / number of total field part of this model
                        </Text>
                      </Coachmark>
                      <Text type="h200">{name}</Text>
                    </Row>
                  ) : (
                    <Text type="h200">{name}</Text>
                  )}
                </TableCell>
              ))}
            </TableRow>
            {isValidRange(firstVisibleIndex, lastVisibleIndex, 0, displayData.length) &&
              _.range(firstVisibleIndex, lastVisibleIndex).map((index) => {
                const totalField = Object.keys(selectedModel.fields).length.toString();
                return (
                  displayData[index] && (
                    <TableRow highlightOnHover key={displayData[index].sourceId}>
                      <TableCell>
                        <Link
                          type="secondary"
                          onClick={() => {
                            navigate(
                              `/validationWorkflow/${modelId}/${displayData[index].sourceId}`,
                            );
                          }}
                        >
                          {displayData[index].sourceId}
                        </Link>
                      </TableCell>
                      {shouldShowEnhancedView() ? (
                        <>
                          <TableCell>{getPendingReviewers(displayData[index])}</TableCell>
                          <TableCell>{getPendingValidators(displayData[index])}</TableCell>
                          <TableCell>{getReviewedBy(displayData[index])}</TableCell>
                          <TableCell>{getValidatedBy(displayData[index])}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {getGroundTruthDataListViewStatus(
                              displayData[index],
                              selectedModel,
                              alias,
                            )}
                          </TableCell>
                          <TableCell>
                            {getGroundTruthDataListViewReviewer(displayData[index], alias)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  )
                );
              })}
          </Table>
        </Column>
        <PaginationWithDropdown
          currentPage={currentPage}
          totalPages={numberOfPages}
          perPage={itemsPerPage}
          setPerPage={(pageNum) => {
            dispatch(setItemPerPage(pageNum));
            dispatch(setCurrentPage(1));
          }}
          setCurrentPage={(newPage) => dispatch(setCurrentPage(newPage))}
        />
      </Column>
      <Column
        height={"92vh"}
        className="incident-summary"
        alignmentHorizontal="start"
        spacingInset={"500"}
      >
        {(!isAssignedBasedReview(selectedModel) || isAdminUser(ldap)) && (
          <>
            {isAssignedBasedReview(selectedModel) && isAdminUser(ldap) ? (
              <Column height="92vh" overflowY="auto">
                <Text type="h300">Assignment-Based Review Overview</Text>

                {/* Filter dropdown - Full Width */}
                {shouldShowEnhancedView() && (
                  <Select
                    placeholder="Filter by reviewers..."
                    value={aliasFilter}
                    onChange={(selectedValues) => {
                      dispatch(setAliasFilter(selectedValues as string[]));
                      dispatch(applyAliasFilter());
                    }}
                    width="80%"
                  >
                    {aliasOptions.map((option) => (
                      <SelectOption key={option.value} value={option.value} label={option.label} />
                    ))}
                  </Select>
                )}

                {/* Upload Guide Modal */}
                {showUploadGuideModal && (
                  <Modal
                    title="Upload CSV to Assign Review Jobs"
                    open={showUploadGuideModal}
                    onClose={() => {
                      setShowUploadGuideModal(false);
                      setAssignmentCSV(null);
                    }}
                    width="700px"
                  >
                    <Column spacing="400">
                      <Text>
                        Upload a CSV file to assign review jobs to multiple reviewers. Your CSV file
                        should have the following format:
                      </Text>

                      <Column spacing="300">
                        <Text type="b100">Required CSV Format:</Text>
                        <Table showDividers={true} headerRows={1}>
                          <TableRow backgroundColor={meridianColors.colorGray50}>
                            <TableCell>
                              <Text type="h200">source_id</Text>
                            </TableCell>
                            <TableCell>
                              <Text type="h200">reviewers</Text>
                            </TableCell>
                            <TableCell>
                              <Text type="h200">admin_reviewers</Text>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Text>550e8400-e29b-41d4-a716-446655440001</Text>
                            </TableCell>
                            <TableCell>
                              <Text>alias1;alias2</Text>
                            </TableCell>
                            <TableCell>
                              <Text>admin_alias1</Text>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Text>550e8400-e29b-41d4-a716-446655440002</Text>
                            </TableCell>
                            <TableCell>
                              <Text>alias2</Text>
                            </TableCell>
                            <TableCell>
                              <Text>admin_alias1;admin_alias2</Text>
                            </TableCell>
                          </TableRow>
                        </Table>
                      </Column>

                      <Column spacing="200">
                        <Text type="b100">Column Descriptions:</Text>
                        <Text>
                          • <strong>source_id</strong>: The ground truth item UUID to assign (must
                          match existing source IDs)
                        </Text>
                        <Text>
                          • <strong>reviewers</strong>: Regular reviewers (separate multiple with
                          semicolon)
                        </Text>
                        <Text>
                          • <strong>admin_reviewers</strong>: Admin validators (separate multiple
                          with semicolon)
                        </Text>
                      </Column>

                      <FileInput
                        type="single"
                        accept=".csv"
                        uploadButtonType="primary"
                        uploadButtonLabel="Select CSV File"
                        onFileAttached={(files) => {
                          setShowUploadGuideModal(false);
                          handleCSVUpload(files);
                        }}
                      />
                    </Column>

                    <ModalFooter>
                      <Button
                        type="tertiary"
                        onClick={() => {
                          setShowUploadGuideModal(false);
                          setAssignmentCSV(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </ModalFooter>
                  </Modal>
                )}

                {/* Assignment Preview Modal */}
                {showPreviewModal && (
                  <Modal
                    title="Assignment Preview"
                    open={showPreviewModal}
                    onClose={() => {
                      setShowPreviewModal(false);
                      setAssignmentCSV(null);
                    }}
                    width="600px"
                  >
                    <Column spacing="400">
                      <Text>
                        Review the assignments below before confirming. This will assign tasks to
                        the following reviewers:
                      </Text>

                      <Table showDividers={true} headerRows={1}>
                        <TableRow backgroundColor={meridianColors.colorGray50}>
                          <TableCell>
                            <Text type="h200">Reviewer</Text>
                          </TableCell>
                          <TableCell>
                            <Text type="h200">Review Tasks</Text>
                          </TableCell>
                          <TableCell>
                            <Text type="h200">Admin Tasks</Text>
                          </TableCell>
                          <TableCell>
                            <Text type="h200">Total Tasks</Text>
                          </TableCell>
                        </TableRow>
                        {assignmentPreview.map((item) => (
                          <TableRow key={item.reviewer}>
                            <TableCell>
                              <Text>{item.reviewer}</Text>
                            </TableCell>
                            <TableCell>
                              <Text>{item.regularTasks}</Text>
                            </TableCell>
                            <TableCell>
                              <Text>{item.adminTasks}</Text>
                            </TableCell>
                            <TableCell>
                              <Text type="b100">{item.totalTasks}</Text>
                            </TableCell>
                          </TableRow>
                        ))}
                      </Table>
                    </Column>

                    <ModalFooter>
                      <Button
                        type="tertiary"
                        onClick={() => {
                          setShowPreviewModal(false);
                          setAssignmentCSV(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleConfirmAssignment}>Confirm Assignment</Button>
                    </ModalFooter>
                  </Modal>
                )}

                {/* Update Assignment Modal */}
                {showUpdateAssignmentModal && (
                  <Modal
                    title="Update Reviewer Assignment"
                    open={showUpdateAssignmentModal}
                    onClose={() => {
                      setShowUpdateAssignmentModal(false);
                      setUpdateFormData({
                        oldReviewer: "",
                        newReviewer: "",
                        assignmentType: "Replace",
                      });
                    }}
                    width="500px"
                  >
                    <Column spacing="400">
                      <Text>
                        Update reviewer assignments across all ground truth items. This will affect
                        all items assigned to the selected reviewer.
                      </Text>

                      <Column spacing="300">
                        <Text type="b100">Assignment Type:</Text>
                        <Select
                          value={updateFormData.assignmentType}
                          onChange={(value) => {
                            setUpdateFormData((prev) => ({
                              ...prev,
                              assignmentType: value as "Replace" | "Remove",
                              newReviewer: value === "Remove" ? "" : prev.newReviewer,
                            }));
                          }}
                        >
                          <SelectOption value="Replace" label="Replace reviewer with another" />
                          <SelectOption
                            value="Remove"
                            label="Remove reviewer from all assignments"
                          />
                        </Select>

                        <Text type="b100">Current Reviewer to Update:</Text>
                        <Select
                          placeholder="Select reviewer to update..."
                          value={updateFormData.oldReviewer}
                          onChange={(value) => {
                            setUpdateFormData((prev) => ({
                              ...prev,
                              oldReviewer: value as string,
                            }));
                          }}
                        >
                          {aliasOptions.map((option) => (
                            <SelectOption
                              key={option.value}
                              value={option.value}
                              label={option.label}
                            />
                          ))}
                        </Select>

                        {updateFormData.assignmentType === "Replace" && (
                          <>
                            <Text type="b100">New Reviewer:</Text>
                            <Input
                              placeholder="Enter new reviewer alias..."
                              value={updateFormData.newReviewer}
                              onChange={(value) => {
                                setUpdateFormData((prev) => ({ ...prev, newReviewer: value }));
                              }}
                            />
                          </>
                        )}
                      </Column>
                    </Column>

                    <ModalFooter>
                      <Button
                        type="tertiary"
                        onClick={() => {
                          setShowUpdateAssignmentModal(false);
                          setUpdateFormData({
                            oldReviewer: "",
                            newReviewer: "",
                            assignmentType: "Replace",
                          });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          if (modelId && updateFormData.oldReviewer) {
                            dispatch(
                              updateAssignment({
                                modelId: modelId,
                                assignmentType:
                                  ReviewerAssignmentTypeEnum[
                                    updateFormData.assignmentType.toUpperCase() as keyof typeof ReviewerAssignmentTypeEnum
                                  ],
                                oldReviewer: updateFormData.oldReviewer,
                                newReviewer:
                                  updateFormData.assignmentType === "Replace"
                                    ? updateFormData.newReviewer
                                    : undefined,
                              }),
                            );
                            setShowUpdateAssignmentModal(false);
                            setUpdateFormData({
                              oldReviewer: "",
                              newReviewer: "",
                              assignmentType: "Replace",
                            });
                          }
                        }}
                        disabled={
                          !updateFormData.oldReviewer ||
                          (updateFormData.assignmentType === "Replace" &&
                            !updateFormData.newReviewer)
                        }
                      >
                        Update Assignment
                      </Button>
                    </ModalFooter>
                  </Modal>
                )}

                {/* Dynamic Progress based on filter selection */}
                <DonutChart
                  totalCount={calculateMetrics.totalCount}
                  reviewedCount={calculateMetrics.completedCount}
                  isLoading={getListViewLoading === "pending"}
                  title={calculateMetrics.title}
                  subtitle={calculateMetrics.subtitle}
                />
                {/* Review Workflow Metrics - White Background Container */}
                <Row spacingInset="400" className="donut-chart" width={"80%"}>
                  <Column spacing="400">
                    <Text type="h200">Review Workflow Metrics</Text>
                    <Row spacing="600">
                      <Column spacing="200">
                        <Text type="h100">Reviews:</Text>
                        <Text>
                          Assigned:{" "}
                          {Object.values(assignedReviewTasks).reduce(
                            (sum, count) => sum + count,
                            0,
                          )}
                        </Text>
                        <Text>
                          Pending:{" "}
                          {Object.values(pendingReviewTasks).reduce((sum, count) => sum + count, 0)}
                        </Text>
                        <Text>
                          Completed:{" "}
                          {Object.values(assignedReviewTasks).reduce((sum, count, index) => {
                            const pendingForUser = Object.values(pendingReviewTasks)[index] || 0;
                            return sum + (count - pendingForUser);
                          }, 0)}
                        </Text>
                      </Column>

                      <Column spacing="200">
                        <Text type="h100">Admin Validations</Text>
                        <Text>
                          Assigned:{" "}
                          {Object.values(assignedValidationTasks).reduce(
                            (sum, count) => sum + count,
                            0,
                          )}
                        </Text>
                        <Text>
                          Pending:{" "}
                          {Object.values(pendingValidationTasks).reduce(
                            (sum, count) => sum + count,
                            0,
                          )}
                        </Text>
                        <Text>
                          Completed:{" "}
                          {Object.values(assignedValidationTasks).reduce((sum, count, index) => {
                            const pendingForUser =
                              Object.values(pendingValidationTasks)[index] || 0;
                            return sum + (count - pendingForUser);
                          }, 0)}
                        </Text>
                      </Column>
                    </Row>
                  </Column>
                </Row>

                {/* Reviewer Progress Details - White Background Container */}
                <Row spacingInset="400" className="donut-chart" width={"80%"}>
                  <Column spacing="400">
                    <Text type="h200">Reviewer Progress Details</Text>
                    <Text type="h100">Reviewers:</Text>
                    {Object.keys(assignedReviewTasks)
                      .filter((alias) => assignedReviewTasks[alias] > 0)
                      .map((alias) => {
                        const completed = assignedReviewTasks[alias] - pendingReviewTasks[alias];
                        const total = assignedReviewTasks[alias];
                        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
                        return (
                          <Row key={`review-${alias}`} spacing="400" widths={["30%", "70%"]}>
                            <Text>{alias}:</Text>
                            <Text>
                              {completed}/{total} ({percentage}%)
                            </Text>
                          </Row>
                        );
                      })}

                    <Column spacing="300">
                      <Text type="h100">Admin Validators:</Text>
                    </Column>
                    {Object.keys(assignedValidationTasks)
                      .filter((alias) => assignedValidationTasks[alias] > 0)
                      .map((alias) => {
                        const completed =
                          assignedValidationTasks[alias] - pendingValidationTasks[alias];
                        const total = assignedValidationTasks[alias];
                        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
                        return (
                          <Row key={`validation-${alias}`} spacing="400" widths={["30%", "70%"]}>
                            <Text>{alias}:</Text>
                            <Text>
                              {completed}/{total} ({percentage}%)
                            </Text>
                          </Row>
                        );
                      })}
                  </Column>
                </Row>
              </Column>
            ) : (
              <>
                <Text type="h300">Overall Ground Truth Summary</Text>
                <DonutChart
                  totalCount={groundTruthDataTotalCount}
                  reviewedCount={groundTruthDataTotalReviewedCount}
                  isLoading={getListViewLoading === "pending"}
                  title="Total Ground Truth"
                  subtitle="Remaining cases for review"
                />
              </>
            )}
          </>
        )}

        {isAssignedBasedReview(selectedModel) &&
          (assignedReviewTasks[alias] > 0 || assignedValidationTasks[alias]) && (
            <>
              <Text type="h300">Assigned Ground Truth Task Summary</Text>
              {assignedReviewTasks[alias] > 0 && (
                <>
                  <DonutChart
                    totalCount={assignedReviewTasks[alias]}
                    reviewedCount={assignedReviewTasks[alias] - pendingReviewTasks[alias]}
                    isLoading={getListViewLoading === "pending"}
                    title="Total Labeling Task"
                    subtitle="Remaining cases for review"
                  />
                </>
              )}
              {assignedValidationTasks[alias] > 0 && (
                <>
                  <DonutChart
                    totalCount={assignedValidationTasks[alias] || 0}
                    reviewedCount={
                      assignedValidationTasks[alias] - pendingValidationTasks[alias] || 0
                    }
                    isLoading={getListViewLoading === "pending"}
                    title="Total Validation Task"
                    subtitle="Remaining cases for validation"
                  />
                </>
              )}
            </>
          )}
        {/* Download functionality is now inline in the overview section */}
      </Column>
    </Row>
  );
};

export default ModelGroundTruthListView;
